import { useState, useEffect, useCallback, useRef } from 'react'
import { supabase } from '../../../shared/lib/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { usePortalClient } from '../hooks/usePortalClient'
import { logAuditEvent } from '../../../shared/lib/auditLog'

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'policy', label: 'Policy' },
  { value: 'report', label: 'Report' },
  { value: 'contract', label: 'Contract' },
  { value: 'evidence', label: 'Evidence' },
  { value: 'assessment', label: 'Assessment' }
]

const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'text/csv',
  'text/plain',
  'image/png',
  'image/jpeg'
]

const MAX_FILE_SIZE = 25 * 1024 * 1024

function formatFileSize(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileIcon(mimeType) {
  if (mimeType?.includes('pdf')) return 'text-red-500'
  if (mimeType?.includes('word') || mimeType?.includes('document')) return 'text-blue-500'
  if (mimeType?.includes('sheet') || mimeType?.includes('excel') || mimeType?.includes('csv')) return 'text-green-500'
  if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint')) return 'text-orange-500'
  if (mimeType?.includes('image')) return 'text-purple-500'
  return 'text-gray-400'
}

function PortalVault() {
  const { user, profile } = useAuth()
  const { clientId, client, loading: clientLoading } = usePortalClient()
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState('')
  const [uploadCategory, setUploadCategory] = useState('general')
  const fileInputRef = useRef(null)

  const fetchDocuments = useCallback(async () => {
    if (!clientId) return
    try {
      let query = supabase
        .from('client_documents')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: false })

      if (categoryFilter) query = query.eq('category', categoryFilter)

      const { data, error: fetchErr } = await query
      if (fetchErr) throw fetchErr
      setDocuments(data || [])
    } catch (err) {
      console.error('Failed to fetch documents:', err)
    } finally {
      setLoading(false)
    }
  }, [clientId, categoryFilter])

  useEffect(() => {
    if (!clientLoading) fetchDocuments()
  }, [fetchDocuments, clientLoading])

  const handleUpload = async (files) => {
    if (!files || files.length === 0 || !clientId) return
    setError('')
    setUploading(true)

    try {
      for (const file of files) {
        if (!ALLOWED_TYPES.includes(file.type)) {
          setError(`${file.name}: File type not supported. Upload PDF, DOCX, XLSX, PPTX, CSV, TXT, PNG, or JPG files.`)
          continue
        }
        if (file.size > MAX_FILE_SIZE) {
          setError(`${file.name}: File size must be under 25MB.`)
          continue
        }

        const fileId = crypto.randomUUID()
        const filePath = `${clientId}/${fileId}-${file.name}`

        const { error: uploadErr } = await supabase.storage
          .from('client-documents')
          .upload(filePath, file, { contentType: file.type, upsert: false })

        if (uploadErr) throw uploadErr

        const { error: dbErr } = await supabase
          .from('client_documents')
          .insert({
            client_id: clientId,
            file_name: file.name,
            file_path: filePath,
            file_size: file.size,
            mime_type: file.type,
            category: uploadCategory,
            uploaded_by: user.id,
            uploaded_by_role: profile?.role === 'client' ? 'client' : 'admin'
          })

        if (dbErr) throw dbErr

        logAuditEvent({
          category: 'change',
          action: 'document.uploaded',
          description: `Uploaded document: ${file.name}`,
          clientId,
          clientName: client?.name,
          resourceType: 'document',
          metadata: { fileName: file.name, fileSize: file.size, docCategory: uploadCategory }
        })
      }

      await fetchDocuments()
    } catch (err) {
      setError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleDownload = async (doc) => {
    try {
      const { data, error: dlErr } = await supabase.storage
        .from('client-documents')
        .createSignedUrl(doc.file_path, 60)

      if (dlErr) throw dlErr
      window.open(data.signedUrl, '_blank')
    } catch (err) {
      setError('Download failed: ' + err.message)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)
    handleUpload(e.dataTransfer.files)
  }

  const categoryCounts = documents.reduce((acc, d) => {
    acc[d.category] = (acc[d.category] || 0) + 1
    return acc
  }, {})

  if (clientLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white">Document Vault</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Securely upload and share documents with your advisory team. All files are encrypted at rest.
        </p>
      </div>

      {/* Category cards */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
        {CATEGORIES.map(cat => (
          <button
            key={cat.value}
            onClick={() => setCategoryFilter(categoryFilter === cat.value ? '' : cat.value)}
            className={`p-3 rounded-lg text-center transition-colors border ${
              categoryFilter === cat.value
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
            }`}
          >
            <p className={`text-lg font-bold ${categoryFilter === cat.value ? 'text-primary-600 dark:text-primary-400' : 'text-gray-900 dark:text-white'}`}>
              {categoryCounts[cat.value] || 0}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{cat.label}</p>
          </button>
        ))}
      </div>

      {/* Upload area with integrated category selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Category selector row */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Upload to:</label>
          <div className="flex gap-1.5">
            {CATEGORIES.map(c => (
              <button
                key={c.value}
                type="button"
                onClick={(e) => { e.stopPropagation(); setUploadCategory(c.value) }}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  uploadCategory === c.value
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {/* Drop zone */}
        <div
          className={`p-8 text-center transition-colors cursor-pointer ${
            dragOver ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.png,.jpg,.jpeg"
            onChange={(e) => handleUpload(e.target.files)}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-3 py-2">
              <span className="animate-spin inline-block w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full" />
              <span className="text-sm text-primary-600 dark:text-primary-400 font-medium">Uploading...</span>
            </div>
          ) : (
            <>
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                <span className="font-medium text-primary-600 dark:text-primary-400">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">PDF, DOCX, XLSX, PPTX, CSV, TXT, PNG, JPG (max 25MB)</p>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline text-xs">Dismiss</button>
        </div>
      )}

      {/* Documents list */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
          </div>
        ) : documents.length === 0 ? (
          <div className="py-12 text-center">
            <svg className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
            </svg>
            <p className="text-sm text-gray-500 dark:text-gray-400">No documents yet</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Upload files to share with your advisory team</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {documents.map(doc => (
              <div key={doc.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                <svg className={`w-5 h-5 flex-shrink-0 ${getFileIcon(doc.mime_type)}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <div className="flex-1 min-w-0">
                  <button onClick={() => handleDownload(doc)} className="text-sm font-medium text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors truncate block text-left">
                    {doc.file_name}
                  </button>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-400 dark:text-gray-500 capitalize">{doc.category}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{formatFileSize(doc.file_size)}</span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">{new Date(doc.created_at).toLocaleDateString()}</span>
                    <span className={`text-xs ${doc.uploaded_by_role === 'client' ? 'text-teal-500' : 'text-gray-400 dark:text-gray-500'}`}>
                      {doc.uploaded_by_role === 'client' ? 'You' : 'Advisory Team'}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleDownload(doc)}
                  className="p-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex-shrink-0"
                  title="Download"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Security notice */}
      <div className="rounded-lg p-3 flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
        <svg className="w-4 h-4 flex-shrink-0 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
        <p className="text-xs text-green-700 dark:text-green-400">
          All documents are stored with AES-256 encryption and accessible only to authorized users.
        </p>
      </div>
    </div>
  )
}

export default PortalVault
